import React, { useEffect, useState } from 'react';
import { adminAPI } from '../config/apiService';

// Annual Swag Program — Program Year. Starting a year is mostly: set the
// (drifting) cut-off dates and update the welcome letter, then flip
// ReadyForSelections on when it's time to open ordering.
const TOKENS = ['[Employee Full Name]', '[Employee Name]', '[MaxPoints]',
    '[Unique 5-character Code]', '[Your Supervisor]', '[Core_Image_Path]'];

const toInput = (iso) => (iso ? String(iso).slice(0, 10) : '');
const blank = (year) => ({
    programYearID: null, programYear: year, theme: '',
    startDate: '', cutOffDate: '', finalCutoffDate: '', orderDate: '', distributionDate: '',
    readyForSelections: false, employeeAnnouncementWithCode: '',
});

function ProgramYearManager() {
    const thisYear = new Date().getFullYear();
    const [years, setYears] = useState([]);
    const [f, setF] = useState(blank(thisYear));
    const [showPreview, setShowPreview] = useState(false);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(''); const [msgKind, setMsgKind] = useState('info');
    const flash = (t, k = 'info') => { setMsg(t); setMsgKind(k); };
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

    const load = async () => {
        try {
            const r = await adminAPI.getProgramYears();
            const list = r.data || [];
            setYears(list);
            // default to the newest year on first load
            if (list.length && f.programYearID == null && !f.employeeAnnouncementWithCode) pick(list[0]);
        } catch (e) { flash(`Load failed: ${e?.response?.data || e?.message}`, 'error'); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const pick = (y) => setF({
        programYearID: y.programYearID, programYear: y.programYear, theme: y.theme || '',
        startDate: toInput(y.startDate), cutOffDate: toInput(y.cutOffDate),
        finalCutoffDate: toInput(y.finalCutoffDate), orderDate: toInput(y.orderDate),
        distributionDate: toInput(y.distributionDate),
        readyForSelections: !!y.readyForSelections,
        employeeAnnouncementWithCode: y.employeeAnnouncementWithCode || '',
    });

    const onSelectYear = (v) => {
        if (v === 'new') { setF(blank(thisYear)); return; }
        const y = years.find((x) => x.programYearID === Number(v));
        if (y) pick(y);
    };

    const save = async () => {
        if (!f.programYear) return flash('Program year is required.', 'error');
        setBusy(true); setMsg('');
        try {
            const res = await adminAPI.saveProgramYear({
                ProgramYearID: f.programYearID,
                ProgramYear: Number(f.programYear),
                Theme: f.theme || null,
                StartDate: f.startDate || null,
                CutOffDate: f.cutOffDate || null,
                FinalCutoffDate: f.finalCutoffDate || null,
                OrderDate: f.orderDate || null,
                DistributionDate: f.distributionDate || null,
                ReadyForSelections: f.readyForSelections,
                EmployeeAnnouncementWithCode: f.employeeAnnouncementWithCode || null,
            });
            flash(`Saved (year ${f.programYear}).`, 'success');
            const list = (await adminAPI.getProgramYears()).data || [];
            setYears(list);
            const saved = list.find((x) => x.programYearID === res.data.programYearID);
            if (saved) pick(saved);
        } catch (e) { flash(`Save failed: ${e?.response?.data || e?.message}`, 'error'); }
        finally { setBusy(false); }
    };

    return (
        <div style={styles.wrap}>
            <div style={styles.eyebrow}>Annual Swag Program</div>
            <h2 style={styles.h2}>Program Year</h2>
            <p style={styles.muted}>Set the year&rsquo;s cut-off dates and welcome letter. Flip &ldquo;Ready for selections&rdquo; on to open ordering.</p>

            {msg && <Banner kind={msgKind}>{msg}</Banner>}

            <div style={styles.card}>
                <div style={styles.row}>
                    <Field label="Year" w={140}>
                        <select value={f.programYearID ?? 'new'} onChange={(e) => onSelectYear(e.target.value)} style={styles.input}>
                            <option value="new">+ New year</option>
                            {years.map((y) => (
                                <option key={y.programYearID} value={y.programYearID}>
                                    {y.programYear}{y.readyForSelections ? ' · open' : ''}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Program year (number)" w={160}>
                        <input type="number" value={f.programYear} onChange={(e) => set('programYear', e.target.value)} style={styles.input} disabled={f.programYearID != null} />
                    </Field>
                    <Field label="Theme" w={320}>
                        <input value={f.theme} onChange={(e) => set('theme', e.target.value)} placeholder="optional" style={styles.input} />
                    </Field>
                </div>

                <div style={styles.subhead}>Dates <span style={styles.hint}>(these usually drift each year)</span></div>
                <div style={styles.row}>
                    <DateField label="Start" v={f.startDate} on={(v) => set('startDate', v)} />
                    <DateField label="Employee cut-off" v={f.cutOffDate} on={(v) => set('cutOffDate', v)} />
                    <DateField label="Final (manager) cut-off" v={f.finalCutoffDate} on={(v) => set('finalCutoffDate', v)} />
                    <DateField label="Order date" v={f.orderDate} on={(v) => set('orderDate', v)} />
                    <DateField label="Distribution" v={f.distributionDate} on={(v) => set('distributionDate', v)} />
                </div>

                <label style={styles.ready}>
                    <input type="checkbox" checked={f.readyForSelections} onChange={(e) => set('readyForSelections', e.target.checked)} />
                    <span><strong>Ready for selections</strong> — employees can order (leave off while you prep dates + letter)</span>
                </label>

                <div style={styles.subhead}>
                    Welcome letter <span style={styles.hint}>(HTML — shown to each employee with their code)</span>
                    <button onClick={() => setShowPreview((s) => !s)} style={{ ...styles.smallBtn, marginLeft: 10 }}>{showPreview ? 'Edit HTML' : 'Preview'}</button>
                </div>
                <div style={styles.tokens}>
                    Tokens: {TOKENS.map((t) => <code key={t} style={styles.token}>{t}</code>)}
                </div>
                {showPreview ? (
                    <div style={styles.previewBox} dangerouslySetInnerHTML={{ __html: f.employeeAnnouncementWithCode || '<em>empty</em>' }} />
                ) : (
                    <textarea
                        value={f.employeeAnnouncementWithCode}
                        onChange={(e) => set('employeeAnnouncementWithCode', e.target.value)}
                        rows={16}
                        spellCheck={true}
                        style={styles.textarea}
                        placeholder="<p>Good afternoon [Employee Full Name], …</p>"
                    />
                )}

                <div style={{ marginTop: 14 }}>
                    <button onClick={save} disabled={busy} style={btn('#2563eb', busy)}>{busy ? 'Saving…' : (f.programYearID ? 'Save changes' : 'Create year')}</button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, w, children }) {
    return <div style={{ minWidth: w || 160 }}><div style={styles.label}>{label}</div>{children}</div>;
}
function DateField({ label, v, on }) {
    return <div><div style={styles.label}>{label}</div><input type="date" value={v} onChange={(e) => on(e.target.value)} style={styles.input} /></div>;
}
function Banner({ kind, children }) {
    const color = kind === 'error' ? '#dc2626' : kind === 'success' ? '#15803d' : '#374151';
    const bg = kind === 'error' ? '#fef2f2' : kind === 'success' ? '#f0fdf4' : '#f3f4f6';
    return <div style={{ margin: '12px 0', padding: '8px 12px', borderRadius: 4, background: bg, color, fontSize: 13 }}>{children}</div>;
}
const btn = (bg, disabled) => ({ background: disabled ? '#cbd5e1' : bg, color: 'white', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: disabled ? 'default' : 'pointer' });

const styles = {
    wrap: { padding: 24, maxWidth: 920 },
    eyebrow: { fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: '#9ca3af', fontWeight: 700 },
    h2: { margin: '2px 0 4px', fontSize: 22, fontWeight: 800, color: '#111827' },
    muted: { color: '#6b7280', fontSize: 13, lineHeight: 1.5, margin: '4px 0 12px' },
    card: { background: 'white', padding: 18, borderRadius: 10, border: '1px solid #e5e7eb' },
    row: { display: 'flex', gap: 12, flexWrap: 'wrap' },
    subhead: { fontSize: 13, fontWeight: 800, color: '#111827', margin: '16px 0 8px', display: 'flex', alignItems: 'center' },
    label: { fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 },
    hint: { fontSize: 12, color: '#9ca3af', fontWeight: 500, marginLeft: 6 },
    input: { width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
    ready: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 13, color: '#374151', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px' },
    tokens: { fontSize: 12, color: '#6b7280', margin: '2px 0 6px' },
    token: { background: '#f3f4f6', borderRadius: 4, padding: '1px 5px', marginRight: 5, fontSize: 11 },
    textarea: { width: '100%', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12, padding: 10, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box', lineHeight: 1.5 },
    previewBox: { border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, background: '#fff', maxHeight: 460, overflow: 'auto' },
    smallBtn: { background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
};

export default ProgramYearManager;
